/**
 * Orders Page - Premium UI/UX Design (With Auto-Refresh)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiRefreshCw } from 'react-icons/fi';
import './OrdersPage.css';
import { ordersAPI } from '../../services/api';
import { isAuthenticated } from '../../services/api';
import { useToast } from '../../Components/Toast/Toast';

// Status configurations
const STATUS_CONFIG = {
  pending: { 
    label: 'Pending', 
    color: '#f59e0b', 
    bg: '#fef3c7', 
    icon: '⏳',
  },
  processing: { 
    label: 'Processing', 
    color: '#3b82f6', 
    bg: '#dbeafe', 
    icon: '⚙️',
  },
  completed: { 
    label: 'Completed', 
    color: '#10b981', 
    bg: '#d1fae5', 
    icon: '✓',
  },
  failed: { 
    label: 'Failed', 
    color: '#ef4444', 
    bg: '#fee2e2', 
    icon: '✕',
  },
  cancelled: { 
    label: 'Cancelled', 
    color: '#6b7280', 
    bg: '#f3f4f6', 
    icon: '⊘',
  },
};

// Skeleton loader component
const OrderSkeleton = () => (
  <div className="order-card skeleton">
    <div className="skeleton-header">
      <div className="skeleton-line w-40"></div>
      <div className="skeleton-badge"></div>
    </div>
    <div className="skeleton-body">
      <div className="skeleton-line w-60"></div>
      <div className="skeleton-line w-30"></div>
    </div>
    <div className="skeleton-footer">
      <div className="skeleton-btn"></div>
      <div className="skeleton-btn"></div>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ filter, onBrowse }) => (
  <div className="empty-state">
    <div className="empty-icon">📋</div>
    <h3>No orders found</h3>
    <p>
      {filter 
        ? `You don't have any ${filter} orders yet.` 
        : "You haven't placed any orders yet. Start by exploring our services!"}
    </p>
    <button onClick={onBrowse} className="btn-primary">
      <span>🛍️</span> Browse Services
    </button>
  </div>
);

// Order Card Component
const OrderCard = ({ order, onView, onUpload, onCancel, onDownload }) => {
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return `Today at ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const services = (order.items || []).map(item => item.serviceName || item.service?.name).filter(Boolean);

  return (
    <div 
      className={`order-card ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {order.status === 'completed' && (
        <div className="completion-ribbon">
          <span>✓ Ready</span>
        </div>
      )}

      <div className="order-card-header">
        <div className="order-id-section">
          <span className="order-number">{order.orderNumber}</span>
          <span className="order-date">{formatDate(order.createdAt)}</span>
        </div>
        <div 
          className={`status-badge ${status.label.toLowerCase()}`}
        >
          <span className="status-icon">{status.icon}</span>
          <span>{status.label}</span>
        </div>
      </div>

      <div className="order-card-body">
        <div className="services-info">
          <h3 className="services-title">
            {services.length === 1 ? services[0] : `${services.length} Services`}
          </h3>
          {services.length > 1 && (
            <p className="services-list">{services.join(' • ')}</p>
          )}
        </div>

        <div className="order-meta">
          <div className="meta-item">
            <span className="meta-label">Total</span>
            <span className="meta-value price">{formatPrice(order.totalAmount)}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Items</span>
            <span className="meta-value">{(order.items || []).reduce((acc, item) => acc + (item.quantity || 1), 0)}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Payment</span>
            <span className="meta-value payment-status">
              {order.paymentStatus === 'paid' ? '✓ Paid' : 'Pending'}
            </span>
          </div>
        </div>

        {order.processingNotes && (
          <div className="processing-notes">
            <span className="notes-icon">📝</span>
            <span>{order.processingNotes}</span>
          </div>
        )}
      </div>

      <div className="order-card-footer">
        <button onClick={() => onView(order._id)} className="btn-view">
          <span>👁️</span> View Details
        </button>

        {order.status === 'pending' && !order.metadata?.allDocumentsUploaded && (
          <button onClick={() => onUpload(order)} className="btn-upload">
            <span>📤</span> Upload Docs
          </button>
        )}
        
        {order.status === 'pending' && order.metadata?.allDocumentsUploaded && (
          <button onClick={() => onView(order._id)} className="btn-processing">
            <span>⏳</span> Awaiting Processing
          </button>
        )}

        {order.status === 'completed' && (
          <button onClick={() => onDownload(order._id)} className="btn-download">
            <span>📥</span> Download
          </button>
        )}

        {['pending', 'processing'].includes(order.status) && (
          <button onClick={() => onCancel(order._id)} className="btn-cancel">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

// Main OrdersPage Component
const OrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [stats, setStats] = useState({ pending: 0, processing: 0, completed: 0, total: 0 });
  
  // Prevent duplicate API calls
  const hasFetched = useRef(false);
  const hasShownMessage = useRef(false);

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // --- NEW: Helper function to fetch orders (reusable) ---
  const fetchOrdersData = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) setLoading(true);
      
      const params = {};
      if (filter) params.status = filter;
      
      const response = await ordersAPI.getUserOrders(params);
      
      if (response.success) {
        const fetchedOrders = response.data?.orders || [];
        
        // Only update state if data has changed (simple length check or deep compare could be added)
        // For now, we update always to ensure status changes are reflected
        setOrders(fetchedOrders);
        
        // Update stats
        if (!filter) {
          setStats({
            pending: fetchedOrders.filter(o => o.status === 'pending').length,
            processing: fetchedOrders.filter(o => o.status === 'processing').length,
            completed: fetchedOrders.filter(o => o.status === 'completed').length,
            total: fetchedOrders.length,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (!isBackgroundRefresh && !hasFetched.current) {
        toast.error('Failed to fetch orders. Please try again.');
      }
    } finally {
      if (!isBackgroundRefresh) setLoading(false);
      hasFetched.current = true;
    }
  };

  // --- NEW: Polling & Initial Fetch Logic ---
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // 1. Initial Fetch
    fetchOrdersData();

    // 2. Set up Polling (Auto-Refresh) every 5 seconds
    const intervalId = setInterval(() => {
      // Check if we have any active orders that need updating
      // We look for any order that is NOT completed, failed, or cancelled
      const hasActiveOrders = orders.some(order => 
        ['pending', 'processing'].includes(order.status)
      );

      // If there are active orders, or if we haven't loaded orders yet, refresh silently
      if (hasActiveOrders || orders.length === 0) {
        // console.log("🔄 Auto-refreshing orders list..."); // Uncomment for debugging
        fetchOrdersData(true); // true = silent refresh (no loading spinner)
      }
    }, 5000);

    // 3. Cleanup on unmount
    return () => clearInterval(intervalId);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, navigate]); // Re-run if filter changes

  // Show success message from navigation state (only once)
  useEffect(() => {
    if (location.state?.message && !hasShownMessage.current) {
      hasShownMessage.current = true;
      toast.success(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.message]);

  const handleRefresh = () => {
    fetchOrdersData(false); // Manual refresh shows loader
    toast.success('Orders refreshed');
  };

  const handleViewOrder = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handleUploadDocuments = (order) => {
    navigate('/upload-documents', { 
      state: { 
        orderId: order._id,
        order: order,
        services: (order.items || []).map(item => item.service).filter(Boolean)
      } 
    });
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const response = await ordersAPI.cancelOrder(orderId);
      if (response.success) {
        toast.success('Order cancelled successfully');
        fetchOrdersData(false); // Refresh immediately
      } else {
        toast.error(response.message || 'Failed to cancel order');
      }
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const handleDownloadResults = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const filters = [
    { key: '', label: 'All Orders', count: stats.total },
    { key: 'pending', label: 'Pending', count: stats.pending },
    { key: 'processing', label: 'Processing', count: stats.processing },
    { key: 'completed', label: 'Completed', count: stats.completed },
  ];

  return (
    <div className="orders-page">
      {/* Hero Section */}
      <div className="orders-hero">
        <div className="hero-content">
          <h1>My Orders</h1>
          <p>Track and manage your document processing orders</p>
        </div>
        <div className="hero-stats">
          <div className="stat-card">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Orders</span>
          </div>
          <div className="stat-card active">
            <span className="stat-number">{stats.processing}</span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-card success">
            <span className="stat-number">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
      </div>

      <div className="orders-container">
        {/* Filters */}
        <div className="filters-section">
          <div className="filters-tabs">
            {filters.map((f) => (
              <button
                key={f.key}
                className={`filter-tab ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                <span>{f.label}</span>
                {f.count > 0 && <span className="filter-count">{f.count}</span>}
              </button>
            ))}
          </div>
          <button 
            className={`refresh-btn ${loading ? 'loading' : ''}`}
            onClick={handleRefresh}
            disabled={loading}
          >
            <FiRefreshCw className="refresh-icon" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Orders Grid */}
        <div className="orders-grid">
          {loading ? (
            <>
              <OrderSkeleton />
              <OrderSkeleton />
              <OrderSkeleton />
            </>
          ) : orders.length === 0 ? (
            <EmptyState 
              filter={filter} 
              onBrowse={() => navigate('/services')} 
            />
          ) : (
            orders.map((order) => (
              <OrderCard
                key={order._id}
                order={order}
                onView={handleViewOrder}
                onUpload={handleUploadDocuments}
                onCancel={handleCancelOrder}
                onDownload={handleDownloadResults}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;